import { useState, useEffect } from "react";
import { toast } from "sonner";
import { adminBankTopup, adminBankWithdraw, getAdminBank, apiError } from "@/lib/api";
import { BankLedger } from "@/components/admin/BankLedger";
import { Landmark, Loader2, AlertTriangle, Plus, Minus, History, ChevronUp } from "lucide-react";

const BANK_REFRESH_MS = 15000;

const BankWarning = ({ balance }) => {
    if (balance > 0) return null;
    return (
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-blaze mt-3" data-testid="bank-warning">
            <AlertTriangle className="w-3.5 h-3.5" />
            Bank depleted — top up to cover user streaming
        </div>
    );
};

const BankBalance = ({ balance, depleted }) => (
    <div>
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
            <Landmark className="w-3.5 h-3.5 text-blaze" />
            Credit Bank
        </div>
        <div
            className={`font-display font-bold text-4xl ${depleted ? "text-blaze" : "text-white"}`}
            data-testid="bank-balance"
        >
            {balance.toLocaleString()}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30 mt-2">
            Debited only as users stream — 2 credits per second
        </div>
        <BankWarning balance={balance} />
    </div>
);

const BankMoveForm = ({ amount, onAmount, busy, onTopUp, onWithdraw }) => (
    <form onSubmit={onTopUp} className="flex gap-2">
        <input
            type="number"
            min="1"
            value={amount}
            onChange={onAmount}
            placeholder="Credits to add"
            className="w-40 bg-obsidian border border-white/15 text-white px-3 py-2.5 font-mono text-xs outline-none focus:border-electric placeholder:text-white/25 transition-colors"
            data-testid="bank-topup-input"
        />
        <button
            type="submit"
            disabled={busy}
            className="px-5 bg-blaze hover:bg-blazeHover disabled:opacity-40 text-white font-mono uppercase text-[11px] tracking-[0.2em] flex items-center gap-2 transition-colors"
            data-testid="bank-topup-button"
        >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Top Up
        </button>
        <button
            type="button"
            onClick={onWithdraw}
            disabled={busy}
            className="px-4 border border-white/20 hover:border-blaze hover:text-blaze disabled:opacity-40 text-white/70 font-mono uppercase text-[11px] tracking-[0.2em] flex items-center gap-2 transition-colors"
            data-testid="bank-withdraw-button"
        >
            <Minus className="w-3.5 h-3.5" />
            Withdraw
        </button>
    </form>
);

const LedgerSection = ({ showLedger, onToggle, ledgerKey }) => (
    <>
        <button
            onClick={onToggle}
            className="mt-5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-white transition-colors"
            data-testid="bank-ledger-toggle"
        >
            {showLedger ? <ChevronUp className="w-3.5 h-3.5" /> : <History className="w-3.5 h-3.5" />}
            {showLedger ? "Hide History" : "History"}
        </button>
        {showLedger && (
            <div className="mt-3 border border-white/10 bg-obsidian">
                <BankLedger key={ledgerKey} />
            </div>
        )}
    </>
);

function useBankMoves(initialBalance) {
    const [balance, setBalance] = useState(initialBalance);
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const [ledgerKey, setLedgerKey] = useState(0);

    useEffect(() => {
        const load = () => getAdminBank().then((d) => setBalance(d.balance)).catch(() => {});
        const timer = setInterval(load, BANK_REFRESH_MS);
        return () => clearInterval(timer);
    }, []);

    const move = async (fn, okMsg) => {
        const value = parseInt(amount, 10);
        if (!value || value <= 0) {
            toast.error("Enter a positive amount of credits");
            return;
        }
        setBusy(true);
        try {
            const res = await fn(value);
            setBalance(res.balance);
            setAmount("");
            setLedgerKey((k) => k + 1);
            toast.success(okMsg(value));
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const topUp = (e) => {
        e.preventDefault();
        move(adminBankTopup, (v) => `+${v.toLocaleString()} credits added to the bank`);
    };

    const withdraw = () => move(adminBankWithdraw, (v) => `-${v.toLocaleString()} credits withdrawn from the bank`);

    return { balance, amount, setAmount, busy, ledgerKey, topUp, withdraw };
}

export const BankCard = ({ initialBalance }) => {
    const bank = useBankMoves(initialBalance);
    const [showLedger, setShowLedger] = useState(false);
    const depleted = bank.balance <= 0;

    return (
        <div
            className={`border bg-surface p-6 mb-6 ${depleted ? "border-blaze" : "border-white/10"}`}
            data-testid="bank-card"
        >
            <div className="flex flex-wrap items-end justify-between gap-6">
                <BankBalance balance={bank.balance} depleted={depleted} />
                <BankMoveForm
                    amount={bank.amount}
                    onAmount={(e) => bank.setAmount(e.target.value)}
                    busy={bank.busy}
                    onTopUp={bank.topUp}
                    onWithdraw={bank.withdraw}
                />
            </div>
            <LedgerSection
                showLedger={showLedger}
                onToggle={() => setShowLedger((v) => !v)}
                ledgerKey={bank.ledgerKey}
            />
        </div>
    );
};
